<template>
  <div class="templates">
    <b-loading :active="isParentLoading" :is-full-page="false"></b-loading>

    <OmxList
        :title="$t('templates-view.title')"
        :data="filteredTemplatesData"
        :loading="isLoading"
        :search-value.sync="search"
        :empty-icon="hasSearch ? 'filter-variant' : 'content-duplicate'"
        :empty-title="hasSearch ? $t('omx-list.empty-title') : $t('templates-empty.title')"
        :empty-body="hasSearch ? null : $t('templates-empty.body')"
        :skeleton-columns="[2, 1.2]"
    >
      <template #actions>
        <b-button @click="backClicked" icon-left="arrow-left">{{ $t('common.back') }}</b-button>
      </template>

      <template #empty-actions>
        <b-button v-if="hasSearch" @click="search = ''">{{ $t('omx-list.clear-selection') }}</b-button>
      </template>

      <b-table-column field="template.code" :label="$t('templates-view.template-code-column')" v-slot="props" sortable>
        <a :href="projectDetailsLink(props.row.template.document_id)" class="omx-row-title">
          {{ props.row.template.code }}
        </a>
      </b-table-column>

      <b-table-column v-slot="props" cell-class="has-text-right">
        <b-button
            v-if="currentUserAccess('ProjectTemplates', 'CREATE')"
            type="is-primary"
            size="is-small"
            class="m-1"
            icon-left="plus-thick"
            @click="createProjectFromTemplateClicked(props.row.template.document_id)"
        >{{ $t('templates-view.create-project') }}</b-button>
        <b-button
            v-if="currentUserAccess('ProjectTemplates', 'DELETE')"
            type="is-danger"
            size="is-small"
            class="m-1"
            icon-left="delete"
            @click="deleteTemplateClicked(props.row.template.document_id)"
        >{{ $t('templates-view.delete-template-title') }}</b-button>
      </b-table-column>
    </OmxList>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "Templates",
  components: { OmxList },
  data() {
    return {
      templatesData: [],
      isLoading: false,
      isParentLoading: false,
      search: ""
    };
  },
  computed: {
    hasSearch() { return this.search.trim() !== ""; },
    filteredTemplatesData() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.templatesData;
      return this.templatesData.filter(row => (row.template.code || "").toLowerCase().includes(q));
    }
  },
  methods: {
    readTemplatesData() {
      this.isLoading = true;
      this.templatesData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getProjectsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getProjectsFunction({ action: "get_templates" })
          .then((result) => {
            result.data["projects"].forEach((template) => {
              this.templatesData.push({ template });
            });
            this.isLoading = false;
          })
          .catch((error) => {
            this.showError(this.$t('messages.save-error'));
            console.error("Error reading templates: ", error?.code);
            this.isLoading = false;
          });
    },
    projectDetailsLink(projectId) {
      if (this.currentUserAccess("ProjectDetails", 'VIEW', projectId))
        return "#/projects/" + projectId;
      return "#/projects/";
    },
    showProjectDetails(projectId) {
      this.$router.push({ name: 'ProjectDetails', params: { id: projectId } });
    },
    deleteTemplate(templateId) {
      this.isParentLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteTemplateFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteTemplateFunction({ action: "delete_project", document_id: templateId })
          .then(() => {
            this.isParentLoading = false;
            this.showSuccess(this.$t('templates-view.template-deleted'));
            this.readTemplatesData();
          })
          .catch((error) => {
            this.isParentLoading = false;
            this.showError(this.$t('messages.save-error'));
            console.error("Error deleting template: ", error?.code);
          });
    },
    async createProjectFromTemplateClicked(templateId) {
      const r = await this.$omxPrompt({
        message: this.$t('templates-view.save-as-project-title').toString(),
        inputAttrs: { type: 'text', placeholder: this.$t('templates-view.project-code-placeholder').toString() },
        confirmText: this.$t('common.save').toString(),
        closeOnConfirm: false
      });
      if (r === null) return;
      const code = (r.value || "").toString().trim();
      if (!code) {
        this.showError(this.$t('messages.required-fields-error'));
        if (r.close) r.close();
        return;
      }
      this.isParentLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const saveAsProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      saveAsProjectFunction({ action: "copy_project", document_id: templateId, code: code, save_as: "project" })
          .then((result) => {
            this.isParentLoading = false;
            if (r.close) r.close();
            this.showSuccess(this.$t('templates-view.save-project-success'));
            const insertedProjectId = result.data.payload["projects"].inserted[0].document_id;
            if (insertedProjectId !== undefined) {
              this.showProjectDetails(insertedProjectId);
            }
          })
          .catch((error) => {
            this.isParentLoading = false;
            this.showError(this.$t('messages.save-error'));
            console.error("Error creating project from template: ", error?.code);
            if (r.close) r.close();
          });
    },
    async deleteTemplateClicked(templateId) {
      const ok = await this.$omxConfirm({
        title: this.$t('templates-view.delete-template-title').toString(),
        message: this.$t('templates-view.delete-template-message').toString(),
        confirmText: this.$t('templates-view.delete-template-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deleteTemplate(templateId);
    },
    backClicked() {
      this.$router.back();
    }
  },
  mounted() {
    if (!this.currentUserAccess("ProjectTemplates", 'VIEW')) {
      this.$router.replace("/projects/").catch(() => { /* navigation duplicated */ });
    }
    this.loadUserRoleFromServer();
    this.readTemplatesData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-row-title {
  color: $omx-brand-600;
  font-weight: 600;
  font-family: $omx-font-mono;
  font-size: 12.5px;
  &:hover { color: $omx-brand-700; text-decoration: underline; }
}
</style>
